/* ==========================================================================
   Shivoham Vastra — theme JavaScript
   Vanilla, no framework. Replaces React/Framer Motion behaviour.
   ========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  /* ------------------------------------------------------------------------
     Utilities
     ---------------------------------------------------------------------- */
  var Theme = (window.Theme = window.Theme || {});

  Theme.formatMoney = function (cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, Theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  };

  Theme.toast = function (message, isError) {
    var el = document.getElementById('theme-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'theme-toast';
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.toggle('toast--error', !!isError);
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 3200);
  };

  /* ------------------------------------------------------------------------
     Header scroll state
     ---------------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 50);
      ticking = false;
    }
    update();
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------------
     Drawers (mobile nav + cart)
     ---------------------------------------------------------------------- */
  var Drawer = {
    lastFocus: null,

    open: function (id) {
      var drawer = document.getElementById(id);
      if (!drawer) return;
      Drawer.lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      var focusable = drawer.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) setTimeout(function () { focusable.focus(); }, 60);
    },

    close: function (id) {
      var drawer = id ? document.getElementById(id) : document.querySelector('.drawer.is-open');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (Drawer.lastFocus) Drawer.lastFocus.focus();
    }
  };

  Theme.Drawer = Drawer;

  function initDrawers() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        Drawer.open(opener.getAttribute('data-drawer-open'));
        return;
      }
      var closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        e.preventDefault();
        Drawer.close(closer.getAttribute('data-drawer-close') || null);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') Drawer.close();
    });
  }

  /* ------------------------------------------------------------------------
     Scroll reveal — replaces Framer Motion whileInView
     ---------------------------------------------------------------------- */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -50px 0px', threshold: 0.05 }
    );

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------------
     Snap-carousel progress bars
     ---------------------------------------------------------------------- */
  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(function (track) {
      var progressId = track.getAttribute('data-carousel-progress');
      var progress = progressId ? document.getElementById(progressId) : null;
      if (!progress) return;
      var bars = progress.querySelectorAll('.carousel-progress__bar');

      function update() {
        var first = track.children[0];
        if (!first) return;
        var itemWidth = first.clientWidth + 16;
        var index = Math.round(track.scrollLeft / itemWidth);
        bars.forEach(function (bar, i) {
          bar.classList.toggle('is-active', i === index);
        });
      }
      update();
      track.addEventListener('scroll', function () {
        window.requestAnimationFrame(update);
      }, { passive: true });
    });
  }

  /* ------------------------------------------------------------------------
     Product gallery
     ---------------------------------------------------------------------- */
  function initGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-gallery-main]');
    if (!main) return;

    gallery.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      e.preventDefault();
      var src = thumb.getAttribute('data-image-src');
      var srcset = thumb.getAttribute('data-image-srcset');
      var alt = thumb.getAttribute('data-image-alt') || '';
      if (src) {
        main.src = src;
        if (srcset) main.srcset = srcset;
        main.alt = alt;
      }
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
        t.classList.toggle('is-active', t === thumb);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Quantity stepper
     ---------------------------------------------------------------------- */
  function initQuantity() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-change]');
      if (!btn) return;
      e.preventDefault();
      var wrap = btn.closest('[data-qty]');
      if (!wrap) return;
      var input = wrap.querySelector('input');
      if (!input) return;
      var min = parseInt(input.getAttribute('min') || '1', 10);
      var delta = parseInt(btn.getAttribute('data-qty-change'), 10);
      var next = Math.max(min, (parseInt(input.value, 10) || min) + delta);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------------
     Variant picker
     ---------------------------------------------------------------------- */
  function initVariantPicker() {
    var picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;

    var dataEl = document.getElementById('variant-data');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }

    // The hidden variant input is a sibling of the picker inside the product form,
    // NOT a descendant of the picker. Scoping this lookup to `picker` silently
    // returned null, which left the form submitting the initial variant forever.
    var pickerForm = picker.closest('[data-product-form]') || document;
    var idInput = pickerForm.querySelector('[data-variant-id]');
    var priceWrap = document.querySelector('[data-price-wrap]');
    var addBtn = document.querySelector('[data-add-to-cart]');
    var stockEl = document.querySelector('[data-stock]');
    var mainImage = document.querySelector('[data-gallery-main]');

    function selectedValues() {
      return Array.prototype.map.call(
        picker.querySelectorAll('[data-option-position]'),
        function (fieldset) {
          var checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        }
      );
    }

    function findVariant(values) {
      return variants.find(function (v) {
        return values.every(function (val, i) {
          return val === null || v.options[i] === val;
        });
      });
    }

    function updateAvailability(values) {
      picker.querySelectorAll('[data-option-position]').forEach(function (fieldset, fi) {
        fieldset.querySelectorAll('input').forEach(function (input) {
          var probe = values.slice();
          probe[fi] = input.value;
          var match = variants.find(function (v) {
            return probe.every(function (val, i) {
              return val === null || v.options[i] === val;
            });
          });
          var available = !!(match && match.available);
          var label = fieldset.querySelector('label[for="' + input.id + '"]');
          if (label) label.classList.toggle('is-unavailable', !available);
          input.disabled = !match;
        });
      });
    }

    function update() {
      var values = selectedValues();
      var variant = findVariant(values);

      updateAvailability(values);

      // Reflect the selected value in each option label
      picker.querySelectorAll('[data-option-position]').forEach(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        var out = fieldset.querySelector('[data-option-selected]');
        if (out && checked) out.textContent = checked.value;
      });

      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = addBtn.getAttribute('data-text-unavailable') || 'Unavailable';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceWrap && variant.price_html) priceWrap.innerHTML = variant.price_html;

      if (stockEl) {
        stockEl.classList.toggle('stock--in', variant.available);
        stockEl.classList.toggle('stock--out', !variant.available);
        var text = stockEl.querySelector('[data-stock-text]');
        if (text) {
          text.textContent = variant.available
            ? stockEl.getAttribute('data-text-in') || 'In Stock'
            : stockEl.getAttribute('data-text-out') || 'Out of Stock';
        }
      }

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? addBtn.getAttribute('data-text-add') || 'Add to Cart'
          : addBtn.getAttribute('data-text-soldout') || 'Sold Out';
      }

      if (Theme.syncBuyBar) Theme.syncBuyBar(variant);

      if (mainImage && variant.featured_image) {
        mainImage.src = variant.featured_image;
        mainImage.removeAttribute('srcset');
      }

      // Keep the URL shareable without a page reload
      if (window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    picker.addEventListener('change', update);
    update();
  }

  /* ------------------------------------------------------------------------
     AJAX cart
     ---------------------------------------------------------------------- */
  function refreshCartCount() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
        });
        if (Theme.renderBundle) Theme.renderBundle(cart.item_count || 0);
        return cart;
      });
  }

  Theme.refreshCartCount = refreshCartCount;

  function renderCartDrawer() {
    var target = document.getElementById('cart-drawer');
    if (!target) return Promise.resolve();

    /* The drawer is a SNIPPET rendered by layout/theme.liquid on every page, not
       a section, so Shopify's Section Rendering API cannot return it. Worse, the
       old code called `?sections=cart-drawer` with Accept: application/json on a
       product URL -- Shopify answers that with the PRODUCT json, so `data[id]`
       was always undefined and the drawer silently kept its page-load markup.
       That is why "Your cart is empty" survived a successful add.
       Re-fetching /cart as HTML gives us freshly rendered drawer markup with no
       new template files, and /cart is the lightest page that renders the
       layout. Caught in production 2026-09-02. */
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart', { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.getElementById('cart-drawer');
        if (!fresh) return;
        var wasOpen = target.classList.contains('is-open');
        target.innerHTML = fresh.innerHTML;
        if (wasOpen) target.classList.add('is-open');
      })
      .catch(function () { /* drawer refresh is non-critical */ });
  }

  function initAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();

      var btn = form.querySelector('[data-add-to-cart]');
      var originalText = btn ? btn.textContent : '';
      var added = false;
      if (btn) {
        btn.disabled = true;
        btn.textContent = btn.getAttribute('data-text-adding') || 'Adding...';
      }

      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: form.querySelector('[data-variant-id]').value,
              quantity: parseInt(form.querySelector('[name="quantity"]') ? form.querySelector('[name="quantity"]').value : '1', 10) || 1
            }
          ]
        })
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart');
            return body;
          });
        })
        .then(function () {
          return Promise.all([refreshCartCount(), renderCartDrawer()]);
        })
        .then(function () {
          /* Confirm the add on the button itself before the drawer slides in, so
             the click always produces visible feedback even if the shopper has
             scrolled the drawer trigger out of view. */
          if (btn) {
            btn.classList.add('is-added');
            btn.textContent = btn.getAttribute('data-text-added') || 'Added \u2713';
          }
          Theme.toast('Added to cart');
          var cartIcon = document.querySelector('[data-cart-count]');
          if (cartIcon) {
            cartIcon.classList.remove('cart-count--bump');
            void cartIcon.offsetWidth;
            cartIcon.classList.add('cart-count--bump');
          }
          setTimeout(function () { Drawer.open('cart-drawer'); }, 260);
          added = true;
        })
        .catch(function (err) {
          Theme.toast(err.message, true);
        })
        .finally(function () {
          if (!btn) return;
          btn.disabled = false;
          if (added) {
            setTimeout(function () {
              btn.classList.remove('is-added');
              btn.textContent = originalText;
            }, 1400);
          } else {
            btn.textContent = originalText;
          }
        });
    });
  }

  function changeCartLine(line, quantity) {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || 'Could not update cart');
          return body;
        });
      });
  }

  function initCartUpdates() {
    // Cart drawer + cart page line changes
    document.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      var line = remove.getAttribute('data-cart-remove');
      changeCartLine(line, 0)
        .then(function () {
          if (document.body.classList.contains('template-cart')) {
            window.location.reload();
          } else {
            return Promise.all([refreshCartCount(), renderCartDrawer()]);
          }
        })
        .catch(function (err) { Theme.toast(err.message, true); });
    });

    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      var line = input.getAttribute('data-cart-qty');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) return;
      changeCartLine(line, qty)
        .then(function () {
          if (document.body.classList.contains('template-cart')) {
            window.location.reload();
          } else {
            return Promise.all([refreshCartCount(), renderCartDrawer()]);
          }
        })
        .catch(function (err) { Theme.toast(err.message, true); });
    });
  }

  /* ------------------------------------------------------------------------
     Collection sort
     ---------------------------------------------------------------------- */
  function initSort() {
    var select = document.querySelector('[data-sort-by]');
    if (!select) return;
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }


  /* ------------------------------------------------------------------------
     Accordions
     ---------------------------------------------------------------------- */
  function setPanel(trigger, open) {
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
  }

  function initAccordions() {
    document.querySelectorAll('[data-accordion-trigger]').forEach(function (t) {
      setPanel(t, t.getAttribute('aria-expanded') === 'true');
    });

    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-accordion-trigger]');
      if (!t) return;
      e.preventDefault();
      setPanel(t, t.getAttribute('aria-expanded') !== 'true');
    });

    // An open panel's height changes when fonts load or the window resizes
    window.addEventListener('resize', function () {
      document.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]').forEach(function (t) {
        setPanel(t, true);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Product detail tabs
     Replaces the stacked accordions on the product page. Delegated so it keeps
     working after a section re-render in the theme editor. Arrow-key roving
     tabindex per the WAI-ARIA tabs pattern.
     ---------------------------------------------------------------------- */
  function selectTab(tab) {
    var group = tab.closest('[data-tabs]');
    if (!group) return;

    var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-tab]'));
    var panels = Array.prototype.slice.call(group.querySelectorAll('[data-tab-panel]'));

    tabs.forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });

    var wanted = tab.getAttribute('aria-controls');
    panels.forEach(function (p) {
      var on = p.id === wanted;
      p.classList.toggle('is-active', on);
      // `hidden` rather than display:none so the CSS fade can re-run on show.
      p.hidden = !on;
    });
  }

  function initTabs() {
    document.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-tab]');
      if (!tab) return;
      e.preventDefault();
      selectTab(tab);
    });

    document.addEventListener('keydown', function (e) {
      var tab = e.target.closest('[data-tab]');
      if (!tab) return;
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;

      var group = tab.closest('[data-tabs]');
      if (!group) return;
      var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-tab]'));
      var i = tabs.indexOf(tab);
      var next;

      if (e.key === 'Home') next = tabs[0];
      else if (e.key === 'End') next = tabs[tabs.length - 1];
      else if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
      else next = tabs[(i - 1 + tabs.length) % tabs.length];

      e.preventDefault();
      selectTab(next);
      next.focus();
    });
  }

  /* ------------------------------------------------------------------------
     Modals (size guide)
     ---------------------------------------------------------------------- */
  function initModals() {
    var lastFocus = null;

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-modal-open]');
      if (opener) {
        e.preventDefault();
        var m = document.getElementById(opener.getAttribute('data-modal-open'));
        if (!m) return;
        lastFocus = opener;
        m.classList.add('is-open');
        m.setAttribute('aria-hidden', 'false');
        document.body.classList.add('drawer-open');
        var close = m.querySelector('[data-modal-close]');
        if (close) setTimeout(function () { close.focus(); }, 60);
        return;
      }

      var closer = e.target.closest('[data-modal-close]');
      if (closer) {
        e.preventDefault();
        var mc = document.getElementById(closer.getAttribute('data-modal-close'));
        if (!mc) return;
        mc.classList.remove('is-open');
        mc.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('drawer-open');
        if (lastFocus) lastFocus.focus();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('.modal.is-open');
      if (!open) return;
      open.classList.remove('is-open');
      open.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (lastFocus) lastFocus.focus();
    });
  }

  /* ------------------------------------------------------------------------
     Bundle progress — reads the live cart, not the local quantity box
     ---------------------------------------------------------------------- */
  function renderBundle(itemCount) {
    var el = document.querySelector('[data-bundle]');
    if (!el) return;
    var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 3;
    var steps = el.querySelectorAll('[data-bundle-step]');
    var hint = el.querySelector('[data-bundle-hint]');

    steps.forEach(function (s, i) { s.classList.toggle('is-filled', i < itemCount); });

    if (!hint) return;
    if (itemCount >= threshold) {
      hint.innerHTML = hint.getAttribute('data-tpl-done') || '';
    } else {
      var remaining = threshold - itemCount;
      var tpl = hint.getAttribute('data-tpl-need') || '';
      hint.innerHTML = tpl.replace('[n]', '<strong>' + remaining + '</strong>');
    }
  }

  Theme.renderBundle = renderBundle;

  function initBundle() {
    if (!document.querySelector('[data-bundle]')) return;
    fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderBundle(cart.item_count || 0); })
      .catch(function () { renderBundle(0); });
  }

  /* ------------------------------------------------------------------------
     Sticky mobile buy bar
     ---------------------------------------------------------------------- */
  function initBuyBar() {
    var bar = document.querySelector('[data-buybar]');
    var form = document.querySelector('[data-product-form]');
    if (!bar || !form) return;

    var addBtn = form.querySelector('[data-add-to-cart]');
    var proxy = bar.querySelector('[data-buybar-add]');

    if (proxy && addBtn) {
      proxy.addEventListener('click', function () { addBtn.click(); });
    }

    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -120px 0px' }
    );
    io.observe(form);
  }

  function syncBuyBar(variant) {
    var priceEl = document.querySelector('[data-buybar-price]');
    if (priceEl && variant && variant.price_html) priceEl.innerHTML = variant.price_html;
    var proxy = document.querySelector('[data-buybar-add]');
    if (proxy && variant) {
      proxy.disabled = !variant.available;
      proxy.textContent = variant.available
        ? proxy.getAttribute('data-text-add') || 'Add to Cart'
        : 'Sold Out';
    }
  }

  Theme.syncBuyBar = syncBuyBar;

  /* ------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function init() {
    initHeader();
    initDrawers();
    initReveal();
    initCarousels();
    initGallery();
    initQuantity();
    initVariantPicker();
    initAddToCart();
    initCartUpdates();
    initSort();
    initAccordions();
    initTabs();
    initModals();
    initBundle();
    initBuyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when the theme editor swaps a section
  document.addEventListener('shopify:section:load', function () {
    initReveal();
    initCarousels();
    initGallery();
    initVariantPicker();
    initHeader();
    initAccordions();
    initTabs();
    initBundle();
    initBuyBar();
  });
})();
